import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  confirmPasswordReset,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../lib/firebase';

/**
 * Firebase Auth の action ハンドラ (公開ルート `/auth/action`、AdminAuthGate の外)。
 *
 * 管理者招待 / PW 再発行で発行されるリンクの着地点。背景・設計は
 * functions/src/admin/manageAdminUsers.ts `toAdminActionLink` 参照。
 *
 * 既定の Firebase ハンドラ (`<project>.firebaseapp.com/__/auth/action`) は
 *   ① admin のカスタムドメインでない
 *   ② パスワード変更後に自動ログインも遷移もしない
 * という問題があるため、招待リンクを oobCode だけ引き継いでこのページへ向け直し、
 * verify → 新パスワード設定 → 自動ログイン → ホーム まで一気通貫で行う。
 *
 * 招待された管理者はまだ未認証なので、本ページは必ず AuthGate の外側に置くこと
 * (apps/admin/src/App.tsx の公開ルート)。
 */

type Stage = 'verifying' | 'form' | 'submitting' | 'error';

/** Firebase Auth エラー code → 日本語メッセージ (verify / confirm 共通)。 */
function authActionErrorMessage(code: string): string {
  if (code.includes('expired-action-code')) {
    return 'このリンクの有効期限が切れています。管理者に再発行を依頼してください。';
  }
  if (code.includes('invalid-action-code')) {
    return 'このリンクは無効です（既に使用済み、または不正なリンクの可能性があります）。管理者に再発行を依頼してください。';
  }
  if (code.includes('user-disabled')) {
    return 'このアカウントは無効化されています。管理者にお問い合わせください。';
  }
  if (code.includes('user-not-found')) {
    return 'アカウントが見つかりません。管理者にお問い合わせください。';
  }
  if (code.includes('weak-password')) {
    return 'パスワードが弱すぎます。8 文字以上で設定してください。';
  }
  return `処理に失敗しました（${code || 'unknown'}）。管理者にお問い合わせください。`;
}

const MIN_PASSWORD_LENGTH = 8;

export function AuthActionPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const mode = params.get('mode') ?? '';
  const oobCode = params.get('oobCode') ?? '';

  const [stage, setStage] = useState<Stage>('verifying');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // ─────────────────────────────── マウント時: oobCode を検証して email を取得
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (mode !== 'resetPassword') {
        // admin の招待 / PW 再発行は resetPassword のみ。verifyEmail 等は未対応。
        setErrorMessage(
          'このリンクの操作はこの画面では扱えません。サインイン画面からやり直してください。',
        );
        setStage('error');
        return;
      }
      if (!oobCode) {
        setErrorMessage('リンクが正しくありません（コードがありません）。');
        setStage('error');
        return;
      }
      try {
        const resolvedEmail = await verifyPasswordResetCode(auth, oobCode);
        if (cancelled) return;
        setEmail(resolvedEmail);
        setStage('form');
      } catch (e) {
        if (cancelled) return;
        const code = (e as { code?: string }).code ?? '';
        setErrorMessage(authActionErrorMessage(code));
        setStage('error');
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  // ─────────────────────────────── 送信: PW 確定 → 自動ログイン → ホーム
  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast('warning', `パスワードは ${MIN_PASSWORD_LENGTH} 文字以上で設定してください`);
      return;
    }
    if (password !== confirm) {
      toast('warning', 'パスワードが一致しません');
      return;
    }
    setStage('submitting');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      // 自動ログイン: 設定したばかりの認証情報でそのままサインイン。
      await signInWithEmailAndPassword(auth, email, password);
      // role / districts の custom claim を即時反映させるため token を強制更新
      // (招待時に server 側で付与済。AdminAuthGate も再読込するが念のため)。
      await auth.currentUser?.getIdToken(true);
      toast('success', 'パスワードを設定しサインインしました');
      // ホームへ。AuthGate がログイン済みを検知し role に応じた画面を表示する。
      navigate('/', { replace: true });
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      setErrorMessage(authActionErrorMessage(code));
      setStage('error');
    }
  }

  return (
    <div className="mx-auto max-w-md py-12">
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-slate-900">CitizenWalk Admin</h1>
        <p className="text-sm text-slate-500 mt-1">管理者パスワードの設定</p>
      </header>

      <Card padding="lg">
        {stage === 'verifying' && <p className="text-sm text-slate-500">リンクを確認しています…</p>}

        {stage === 'error' && (
          <div>
            <p className="text-sm text-red-700 bg-red-50 rounded p-3 border border-red-200">
              {errorMessage}
            </p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="mt-4 text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              サインイン画面へ
            </button>
          </div>
        )}

        {(stage === 'form' || stage === 'submitting') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{email}</span>{' '}
              のパスワードを設定してください。設定後、自動的にサインインします。
            </p>

            <label className="block">
              <span className="text-xs font-semibold text-slate-700">新しいパスワード</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                autoComplete="new-password"
                placeholder={`${MIN_PASSWORD_LENGTH} 文字以上`}
                required
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">パスワード（確認）</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                autoComplete="new-password"
                required
              />
            </label>

            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={stage === 'submitting'}
              className="w-full"
            >
              {stage === 'submitting' ? '設定中…' : 'パスワードを設定してサインイン'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
